import { readonly, shallowRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { addDays, areIntervalsOverlapping } from 'date-fns'

export type CalendarJumpTargetType =
  | 'job'
  | 'project'
  | 'resourceAllocation'
  | 'stockTypeService'
  | 'purchaseOrder'

export interface CalendarJumpTarget {
  type: CalendarJumpTargetType
  id: number | string
  startDate: Date
  endDate: Date
}

export interface CalendarJumpRequest extends CalendarJumpTarget {
  requestId: number
}

const currentJumpRequest = shallowRef<CalendarJumpRequest>()
let nextRequestId = 0

export function useCalendarJumpRequest() {
  return {
    currentJumpRequest: readonly(currentJumpRequest),
  }
}

export function useCalendarJump() {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()

  async function jumpTo(target: CalendarJumpTarget) {
    const targetTimeframe = getTargetTimeframe(target)

    if (route.name !== 'persoplan') {
      await router.push({ name: 'persoplan' })
    }

    store.commit('persoplan/setVisibleDates', targetTimeframe)

    currentJumpRequest.value = {
      ...target,
      requestId: (nextRequestId += 1),
    }
  }

  function getTargetTimeframe(target: CalendarJumpTarget) {
    const visibleTimeframe = {
      startDate: store.state.persoplan.visibleStartDate,
      endDate: store.state.persoplan.visibleEndDate,
    }

    const targetIsInVisibleTimeframe = areIntervalsOverlapping(
      {
        start: target.startDate,
        end: target.endDate,
      },
      {
        start: visibleTimeframe.startDate,
        end: visibleTimeframe.endDate,
      }
    )

    if (
      route.name === 'persoplan' &&
      targetIsInVisibleTimeframe &&
      target.type !== 'stockTypeService'
    ) {
      return visibleTimeframe
    }

    return {
      startDate: target.startDate,
      endDate: addDays(
        target.startDate,
        store.getters['persoplan/numberOfVisibleDays'] - 1
      ),
    }
  }

  return { jumpTo }
}
